import { appendIf, isFunction } from '@btime/helpers'
import { assoc, compose, pick } from 'ramda'
import React from 'react'
import { gql, useMutation } from '@apollo/client'

import { getPlaceLevelIdToSave } from '../../../../utils/helpers/place'
import { placesClient } from '../../../../utils/apollo/api'
import { useCloseModal, useModalData } from '../../../../components/modals/ModalsContext'
import { cleanFieldValuesRequest, validateFieldValues } from '../../../../utils/helpers/fields'

const MUTATION = gql`
  mutation UpsertPlace($input: PlaceInput) {
    upsertPlace(input: $input) {
      id
      name
      resume
      placeLevelsResume
    }
  }
`

const cleanRequest = compose(
  pick(['id', 'name', 'type', 'placeLevelId', 'fieldValues']), //
  item => assoc('placeLevelId', getPlaceLevelIdToSave(item), item),
  item => assoc('fieldValues', cleanFieldValuesRequest(item.fieldValues || []), item)
)

const validate = (form, setErrors) => {
  const errors = [
    ...appendIf(!form.name, { path: ['name'], message: 'Campo obrigatório' }),
    ...appendIf(!form.type, { path: ['type'], message: 'Campo obrigatório' }),
    ...appendIf(!getPlaceLevelIdToSave(form), { path: ['name'], message: 'Selecione pelo menos um nivel' }),
    ...validateFieldValues(form)
  ]

  setErrors(errors)
  return errors.length === 0
}

const SAVE_ERROR_MESSAGE = 'Não foi possível salvar o local. Nenhuma alteração foi gravada, tente novamente.'

// Confere se o que o servidor devolveu bate com o que foi enviado. Só assim a tela de edição fecha
// como "salvo": se o servidor respondeu sem erro mas não gravou, a pessoa precisa ver o aviso.
const confirmSaved = (saved, input) => {
  if (!saved?.id) return false
  if (saved.name !== input.name) return false

  const savedLevels = saved.placeLevelsResume
  if (Array.isArray(savedLevels) && input.placeLevelId) {
    return savedLevels.some(level => Number(level?.id) === Number(input.placeLevelId))
  }

  return true
}

export const useSavePlace = () => {
  const closeModal = useCloseModal()
  const { onSave } = useModalData()
  const [saveError, setSaveError] = React.useState()
  const [request, state] = useMutation(MUTATION, { refetchQueries: ['Places'], client: placesClient })

  const handleRequest = (form, actions) => {
    const input = cleanRequest(form)
    const { setErrors } = actions
    const isValid = validate(form, setErrors)

    if (isValid) {
      setSaveError(undefined)

      return request({ variables: { input } }).then(
        resp => {
          // Erro devolvido pelo servidor: a mensagem aparece no rodapé do formulário (state.error).
          if (resp.errors?.length) return

          const saved = resp.data?.upsertPlace
          if (!confirmSaved(saved, input)) {
            setSaveError({ message: SAVE_ERROR_MESSAGE })
            return
          }

          if (isFunction(onSave)) onSave(saved)
          closeModal()
        },
        // Falha de rede ou de execução: também precisa aparecer para a pessoa.
        error => setSaveError({ message: error?.message || SAVE_ERROR_MESSAGE })
      )
    }
  }

  return [handleRequest, { ...state, error: saveError || state.error }]
}
